/**
 * An action's **shape**: which activation class it carries (Protocol, quick vs full tech), and
 * whether it is attack-shaped, granted, or legal to hold as a prepared action.
 *
 * Kept upstream of {@link import("./actionId")} on purpose — the action-id derivation reads
 * these classifications, so they must not read back into it.
 */

import type { Affordance, EncounterState } from "@lancer-vtt/domain";
import { ok, err } from "@lancer-vtt/shared";
import { gatherActions, type LoadedOverlay } from "../effects/loader";
import { SEED_OVERLAY } from "../effects/seed";
import type { BudgetVerdict } from "./types";

/**
 * The Quick Tech built-ins that route through `request_activate` (Scan / Lock On / Bolster —
 * Invade is a tech attack, `intent.tech`). Exported so the room's status-legality wall can derive
 * "is this request_activate a TECH action?" (Jammed forbids all tech actions) the SAME way
 * `actionIdForIntent` derives the once-per-turn id.
 */
export const QUICK_TECH_ACTIVATE_IDS = new Set(["act_scan", "act_lock_on", "act_bolster"]);

/**
 * **The preparable-action scope, NARROWED to attack-shaped verbs**: the RAW-catalog quick-action
 * ids `prepare` may ready are exactly the ones {@link resolveRelease}
 * (`backend/src/encounterRoom.ts`) can fire back out — **preparable === releasable**, no gap, no
 * overhang. `release_prepare`'s seam only resolves a `RequestAttack`-shaped intent, so the
 * preparable set is the `RequestAttack`-derivable ids: `"skirmish"`, `"ram"`, `"grapple"`,
 * `"improvised_attack"`, and `"quick_tech"` (the Invade route only — see
 * {@link isAttackShapedHeld}, which this set alone cannot fully express because `"quick_tech"` is
 * overloaded). **Deliberately excludes** `"boost"`/`"hide"`/`"search"` and the
 * Scan/Lock-On/Bolster flavor of `"quick_tech"`: they have no `RequestAttack` shape to release
 * through, which would leave an armable-but-unreleasable hold (a prepared Boost that can only
 * lapse) — a trap that violates the hard-enforcement no-dead-end posture. That non-attack-verb
 * prepare+release is a named deferral, alongside the granted-action ("smoke grenade") one.
 * Stabilize/Overcharge (quick, but out of scope) and Full Tech/Barrage (Full actions — RAW never
 * lets Prepare ready those) stay excluded.
 */
export const PREPARABLE_ACTION_IDS = new Set(["skirmish", "ram", "grapple", "improvised_attack", "quick_tech"]);

/**
 * Is `held` **attack-shaped** — does it carry a `RequestAttack`-derivable id that a concrete
 * `RequestAttack` intent can actually derive back, so the hold is reachable through
 * `release_prepare`'s `RequestAttack`-only release seam ({@link resolveRelease
 * `backend/src/encounterRoom.ts`'s `resolveRelease`})? This is the SAME notion `prepare`'s
 * gate ({@link checkPreparableHold}) and `release`'s fire-time id/weapon-match both key on —
 * **preparable iff releasable**. `"skirmish"`/`"ram"`/
 * `"grapple"`/`"improvised_attack"` are attack-shaped by id alone. `"quick_tech"` is the ONE
 * overloaded id — it names both the attack-shaped **Invade** route (`intent.tech &&
 * intent.weapon === "act_invade"`) and the non-attack Scan/Lock-On/Bolster `request_activate`
 * route (`QUICK_TECH_ACTIVATE_IDS`). Only the Invade route is releasable, and (per
 * {@link actionIdForAttackLike}) a `RequestAttack` derives `"quick_tech"` **only** when its
 * weapon is exactly `"act_invade"` — so a `"quick_tech"` hold is attack-shaped **iff** it
 * carries `weapon === "act_invade"`. A `"quick_tech"` hold with no weapon (Scan/Lock-On/
 * Bolster) or some other weapon could never be released (the id-match would fail), so it is
 * NOT attack-shaped — pinning `held.weapon === "act_invade"` here keeps preparable === releasable
 * with no dead-end overhang.
 *
 * **NOTE — attack-shape is necessary but NOT sufficient for a legal prepare.** A weaponless
 * `"skirmish"` IS attack-shaped (releasable — it accepts an arbitrary weapon at release), but
 * preparing it would BYPASS the prepare-time Ordnance/Loading gates (see
 * {@link checkPreparableHold}, which layers that weapon-required rule on top of this
 * predicate). Callers gating a `prepare` must use `checkPreparableHold`, not this alone.
 */
export function isAttackShapedHeld(held: { readiedActionId: string; weapon?: string }): boolean {
  if (!PREPARABLE_ACTION_IDS.has(held.readiedActionId)) return false;
  if (held.readiedActionId === "quick_tech") return held.weapon === "act_invade";
  return true;
}

/**
 * Is this a **granted-action hold** — does the hold's resolved affordance carry a frozen
 * `"round"` or `"scene"` rate, or a Limited pool (`decisions/0202`), and no `"turn"` rate?
 * **Narrower than the full granted-action-prepare space**: an uncapped
 * granted action, or one capped `"turn"` (which would need to interact with `actionUses`/the
 * protocol-safe counter — an unresolved composition), stays deferred and unreleased, the same
 * narrowing the attack-shaped set takes rather than shipping a wider, partly-broken one. The
 * release-side symmetry ({@link ../../../../backend/src/encounterRoom resolveRelease}'s
 * `request_activate` branch) only resolves this SAME grain set, so preparable === releasable
 * holds here too.
 *
 * **It takes the already-resolved record, not `(state, actor)`, and that is a layering decision**
 * (`projects/affordance-table.md` 1.4). The facts used to be read out of the since-deleted
 * `base.actionUsageCaps[held.readiedActionId]` — raw, so a bare granted id found no cap, answered
 * `false`, and dropped `preparePreApplyGate` out of the whole granted ladder (possession was
 * never *checked*; the hold was then rejected by {@link checkPreparableHold}'s catch-all, so
 * nothing could be armed — a closed door, not an open gate). Resolving it here would need
 * `resolveAffordance`, which lives in `economy/carriedActions.ts` — and `carriedActions.ts`
 * already imports {@link isProtocolAction} from THIS file, so importing back would make the
 * `economy/` graph cyclic (the barrel's own doc states that invariant; `effects/loader.ts` keeps a
 * duplicated `bareGrantedId` for the same reason). Taking the record as a **parameter** keeps this
 * a pure lookup that needs no overlay and can stay low in the graph, so neither this function nor
 * {@link checkPreparableHold} has to move files. `refereeCommand.ts`'s `preparePreApplyGate` — the
 * only production caller of either — already holds the overlay and resolves once, feeding the same
 * record to this predicate, to `checkGrantedActionLegality` and to the ownership wall.
 *
 * `undefined` (no record resolved — a caller doing a pure vocabulary check) answers `false`.
 */
export function isGrantedActionHeld(affordance: Affordance | undefined): boolean {
  const cap = affordance?.cap;
  if (cap?.grain === "turn") return false;
  return cap !== undefined || affordance?.limited !== undefined;
}

/**
 * The full **prepare-scope verdict** — the SINGLE rules-layer gate
 * `backend/src/encounterRoom.ts`'s `applyPrepare` calls, so the whole "may this hold be armed?"
 * question lives in one place beside the releasability primitive it builds on. Three legs:
 *
 *   1. **Attack-shaped** ({@link isAttackShapedHeld}) — preparable === releasable; a
 *      non-attack verb (Boost/Hide/Search) or the non-Invade flavor of Quick Tech is rejected
 *      because `release_prepare`'s `RequestAttack`-only seam could never fire it back out (an
 *      armable-but-unreleasable dead-end, against the no-trap posture).
 *   2. **A `"skirmish"` hold MUST name its weapon.** Skirmish is the one preparable verb whose
 *      weapon is a REAL loadout weapon and a FREE variable at release: {@link
 *      actionIdForAttackLike} maps *any* plain weapon → `"skirmish"`, and `resolveRelease`'s
 *      weapon-match is skipped when `held.weapon === undefined`, so a **weaponless** Skirmish
 *      hold accepts an ARBITRARY weapon at release — including an Ordnance one. The
 *      prepare-time Ordnance-window / Loading gates only run when `held.weapon` is named
 *      (`applyPrepare`), and the release path skips the Ordnance re-check by design — so a
 *      weaponless Skirmish would let an Ordnance shot be armed out-of-turn with its firing
 *      window checked NOWHERE ("can't move then prepare to Skirmish with an Ordnance weapon" is
 *      exactly the rule at stake). Requiring the weapon at prepare (this leg) is what makes
 *      those gates bite. The synthetic-weapon verbs (Ram/Grapple/Improvised →
 *      `act_ram`/`act_grapple`/`act_improvised_attack`; Invade → `act_invade`) pin their weapon
 *      THROUGH the id-match itself and carry no Ordnance risk, so they need no real weapon named
 *      — leg 1 already forces Invade's `act_invade`.
 *   3. **OR granted-action-shaped** ({@link isGrantedActionHeld}) — a hold whose resolved
 *      affordance names a frozen round/scene usage cap is preparable too. `ctx` carries that
 *      already-resolved record (the caller runs `resolveAffordance` once — see
 *      `isGrantedActionHeld`'s doc for why the record is passed in rather than re-derived here);
 *      a caller with no `ctx` (a pure vocabulary check) gets the attack-shaped-only verdict. The
 *      room always supplies `ctx`.
 *
 * Returns `ok` when the hold may be armed, else `err(reason)` (the room prepends `"prepare: "`).
 */
export function checkPreparableHold(
  held: { readiedActionId: string; weapon?: string },
  ctx?: { affordance: Affordance | undefined },
): BudgetVerdict {
  if (isAttackShapedHeld(held)) {
    if (held.readiedActionId === "skirmish" && held.weapon === undefined) {
      return err(`readying Skirmish requires naming the weapon (--weapon) so its Ordnance/Loading gates are checked at prepare time`);
    }
    return ok(undefined);
  }
  if (ctx && isGrantedActionHeld(ctx.affordance)) {
    return ok(undefined);
  }
  const label = held.readiedActionId === "quick_tech"
    ? "Quick Tech (only the Invade flavor — --weapon act_invade — is attack-shaped; Scan/Lock On/Bolster are not)"
    : `"${held.readiedActionId}"`;
  return err(
    `v1 can only ready attack-shaped quick actions (Skirmish/Ram/Grapple/Improvised Attack/Invade) or a granted action with a frozen round/scene usage cap (Limited/1-scene) — ${label} is not releasable`,
  );
}

/**
 * The frozen tech-attack activation for `weapon` on `attackerId`, if any
 * (`CombatantBase.techAttackActions`). `undefined` for a builtin tech attack (`act_invade` is
 * never frozen — it's a hardcoded engine constant, not a compendium grant) or a non-tech-attack
 * weapon id. Shared by {@link intentCost} (the `full_tech` cost bump) and {@link
 * actionIdForIntent} (which feeds it to {@link actionIdForAttackLike} as `techActivation`) so both
 * read the SAME frozen fact rather than two independent lookups drifting apart.
 *
 * **Exported** so the attack spine's S2 fact derivation ({@link
 * import("../resolve/attackFacts").deriveAttackFacts}, whose `attackVerb` feeds the S3 pre-roll
 * modifier fold) can derive the in-flight attack's own RAW
 * verb (for `attackScope` — t_juggernaut:1's Ram-scoped buff) the SAME way `actionIdForIntent`
 * does, rather than re-deriving the frozen-activation lookup.
 */
export function frozenTechActivation(
  state: EncounterState | undefined,
  attackerId: string,
  weapon: string | undefined,
): "quick_tech" | "full_tech" | undefined {
  if (weapon === undefined) return undefined;
  return state?.combatants[attackerId]?.base.techAttackActions?.[weapon]?.activation;
}

/**
 * Is the overlay action `actionId` (a granted `request_activate`'s entity id) **Protocol**-
 * activation, per its OVERLAY entry? Protocol-ness reads off the overlay at GATE time — the
 * deliberate opposite of frequency, which must FREEZE at spawn because the overlay carries no
 * frequency metadata to derive from. `false` for an unauthored action (no overlay entry —
 * {@link gatherActions} returns `[]`, never a throw, unlike `resolveActivate`) or one with no
 * `action`-kind Protocol effect.
 */
export function isProtocolAction(ownerId: string, actionId: string, overlay: LoadedOverlay = SEED_OVERLAY): boolean {
  return gatherActions({ ownerId, entityId: actionId, overlay }).some((a) => a.effect.activation === "protocol");
}

/** Is the overlay action `actionId` a **Quick Tech** (`activation: "quick_tech"`), so a Full Tech may take it as a pick? Same overlay read as {@link isProtocolAction}. */
export function isQuickTechAction(ownerId: string, actionId: string, overlay: LoadedOverlay = SEED_OVERLAY): boolean {
  return gatherActions({ ownerId, entityId: actionId, overlay }).some((a) => a.effect.activation === "quick_tech");
}

